<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompanyDataImportModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const form = ref(null);

  const $refs = {
    get form() {
      return form.value;
    },
  };

  const file = ref(null);
  const headered = ref(true);
  const colnum_number = ref(1);
  const colnum_name = ref(2);
  const colnum_email = ref(3);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    file: file.value,
    headered: headered.value,
    colnum_number: colnum_number.value,
    colnum_name: colnum_name.value,
    colnum_email: colnum_email.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'headered' },
      { name: 'colnum_number' },
      { name: 'colnum_name' },
      { name: 'colnum_email' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      file,
      headered,
      colnum_number,
      colnum_name,
      colnum_email,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Import client data') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_kdqbj4xh0_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_5534025912102772_register_j8gcs6dt0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data
        ? $event.data.response.data.updated_records
        : 0,
    };
  };
  const act_5534025912102772_notification_sql249rmt_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One record imported.',
        '${n} records imported.',
        $event.data.resultLength
      ),
    };
  };
  const act_5534025912102772_notification_sql249rmt_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_5534025912102772_notification_qslzzxpt5_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.gettext('Nothing has been imported.'),
    };
  };
  const act_5534025912102772_notification_qslzzxpt5_getWhen = async (
    $event
  ) => {
    return !$event.data.resultLength;
  };
  const act_5534025912102772_closeModal_88nbqf5cm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_5534025912102772_reloadSlickgrid_dpgyhnziw_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_5534025912102772_request_t25fu8n6f_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'import_clients',
      data: {
        header: headered.value,
        file: file.value,
        colnum_number: colnum_number.value,
        colnum_name: colnum_name.value,
        colnum_email: colnum_email.value,
      },
    };
  };
  const act_2434514588044777_closeModal_lq1wxqcyq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_kdqbj4xh0 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_kdqbj4xh0_getActionArgs,
    });
  const inst_evh_7315092382398562_close_upu205qo9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_kdqbj4xh0,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_upu205qo9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_iuq2v73zj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tgv3tdutu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_iuq2v73zj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tgv3tdutu],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ry8v24i3t =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_91ptxg6zg =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ry8v24i3t,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_91ptxg6zg],
    event: 'close',
  });
  const act_5534025912102772_register_j8gcs6dt0 =
    new actionTypes.RegisterAction({
      actionArgs: act_5534025912102772_register_j8gcs6dt0_getActionArgs,
    });
  const inst_evh_5534025912102772_success_9q8yx5n3w =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_register_j8gcs6dt0,
      event: 'success',
      displayName: 'register',
    });
  const act_5534025912102772_notification_sql249rmt =
    new actionTypes.NotificationAction({
      actionArgs: act_5534025912102772_notification_sql249rmt_getActionArgs,
      when: act_5534025912102772_notification_sql249rmt_getWhen,
    });
  const inst_evh_5534025912102772_success_ewpj1y8ay =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_notification_sql249rmt,
      event: 'success',
      displayName: 'notification',
    });
  const act_5534025912102772_notification_qslzzxpt5 =
    new actionTypes.NotificationAction({
      actionArgs: act_5534025912102772_notification_qslzzxpt5_getActionArgs,
      when: act_5534025912102772_notification_qslzzxpt5_getWhen,
    });
  const inst_evh_5534025912102772_success_js0ttfwuq =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_notification_qslzzxpt5,
      event: 'success',
      displayName: 'notification',
    });
  const act_5534025912102772_closeModal_88nbqf5cm =
    new actionTypes.CloseModalAction({
      actionArgs: act_5534025912102772_closeModal_88nbqf5cm_getActionArgs,
    });
  const inst_evh_5534025912102772_success_3aelpf04n =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeModal_88nbqf5cm,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_5534025912102772_reloadSlickgrid_dpgyhnziw =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_5534025912102772_reloadSlickgrid_dpgyhnziw_getActionArgs,
    });
  const inst_evh_5534025912102772_success_ibq32pqmo =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadSlickgrid_dpgyhnziw,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_5534025912102772_request_t25fu8n6f = new actionTypes.RequestAction({
    actionArgs: act_5534025912102772_request_t25fu8n6f_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_5534025912102772_success_9q8yx5n3w,
      inst_evh_5534025912102772_success_ewpj1y8ay,
      inst_evh_5534025912102772_success_js0ttfwuq,
      inst_evh_5534025912102772_success_3aelpf04n,
      inst_evh_5534025912102772_success_ibq32pqmo,
    ],
  });
  const inst_evh_5534025912102772_submit_ae66weaqj =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_request_t25fu8n6f,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_5534025912102772_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_submit_ae66weaqj],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_lq1wxqcyq =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_lq1wxqcyq_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_yn711k338 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_lq1wxqcyq,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_yn711k338],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_upu205qo9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tgv3tdutu,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_91ptxg6zg,
    inst_evh_8564662037462133_close,
    inst_evh_5534025912102772_success_9q8yx5n3w,
    inst_evh_5534025912102772_success_ewpj1y8ay,
    inst_evh_5534025912102772_success_js0ttfwuq,
    inst_evh_5534025912102772_success_3aelpf04n,
    inst_evh_5534025912102772_success_ibq32pqmo,
    inst_evh_5534025912102772_submit_ae66weaqj,
    submit: inst_evh_5534025912102772_submit_ae66weaqj,
    inst_evh_5534025912102772_submit,
    inst_evh_2434514588044777_clickPreventStop_yn711k338,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_upu205qo9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_upu205qo9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tgv3tdutu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tgv3tdutu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_91ptxg6zg = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_91ptxg6zg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_9q8yx5n3w = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_9q8yx5n3w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_ewpj1y8ay = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_ewpj1y8ay.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_js0ttfwuq = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_js0ttfwuq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_3aelpf04n = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_3aelpf04n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_ibq32pqmo = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_ibq32pqmo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit_ae66weaqj = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit_ae66weaqj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_yn711k338 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_yn711k338.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompanyDataImportModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="container">
        <ItpForm
          name="clientDataImport"
          ref="form"
          v-if="loaded"
          @submit="evh_5534025912102772_submit($event, {})"
        >
          <ItpFormGroup
            row
            name="file"
            :label="gettext('File')"
            labelFor="file"
            :label-cols-md="2"
            :content-cols-md="10"
          >
            <ItpFormFile
              v-model="file"
              :value="file"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (file =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              name="file"
              size="md"
              accept=".xlsx, .csv"
              required
            >
            </ItpFormFile>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="header"
            :label-cols-md="0"
            :content-cols-md="12"
          >
            <ItpFormCheckbox
              name="headered"
              :label="gettext('Import file has header')"
              v-model="headered"
              :value="headered"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (headered =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              size="md"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_number"
            :label="gettext('Number column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_number"
              v-model="colnum_number"
              :value="colnum_number"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (colnum_number =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_name"
            :label="gettext('Name column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_name"
              v-model="colnum_name"
              :value="colnum_name"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (colnum_name =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            row
            name="colnum_email"
            :label="gettext('Email column number')"
            :label-cols-md="5"
            :content-cols-md="3"
          >
            <ItpFormInput
              type="number"
              name="colnum_email"
              v-model="colnum_email"
              :value="colnum_email"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (colnum_email =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              size="md"
              number
              required
              :min="1"
            >
            </ItpFormInput>
          </ItpFormGroup>
        </ItpForm>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.clientDataImport && forms.clientDataImport.id"
          :text="gettext('Import')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="
            !forms.clientDataImport || !forms.clientDataImport.submittable
          "
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
